import { mdiDotsHorizontal, mdiRefresh } from '@mdi/js';
import Icon from '@mdi/react';
import { useGetOrderCommentsApi } from 'apis/orders/use-get-order-comments-api';
import { useGetOrderDetailsApi } from 'apis/orders/use-get-order-details-api';
import { ability } from 'configs/ability';
import dayjs from 'dayjs';
import {
  Button,
  Flex,
  FullPageSpinner,
  HStack,
  IconButtonWithTooltip,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  StackProps,
  Text,
  useDisclosure,
  VStack,
  WhiteCard,
} from 'design-system';
import React from 'react';
import { useParams } from 'react-router-dom';
import { CancelOrderModal } from '../components/cancel-order-modal';
import { ConfirmOrderModal } from '../components/confirm-order-modal';
import { OrderComments } from '../components/order-comments';
import { OrderDetailsSection } from '../components/order-details-section';
import { OrderInvoiceModal } from '../components/order-invoice-modal';
import { OrderItemsTable } from '../components/order-items-table';
import { OrderLifeTimeModal } from '../components/order-lifetime-modal';
import { ResendEmailModal } from '../components/resend-email-modal';
import { ResendWhatsappModal } from '../components/resend-whatsapp-modal';
import { RevertOrderModal } from '../components/revert-order-modal';
import { StatusBadgeCell } from '../components/status-badge-cell';
import { orderDetailActions, OrderMoreAction, previousOrderStatus } from '../utils/order-detail-actions';

interface ISectionHeader extends StackProps {
  sectionName: string;
}

/** A card title; `children` (an action or two) sit at the end of the row. */
const SectionHeader = ({ sectionName, children, ...rest }: ISectionHeader) => {
  return (
    <HStack spacing='sm' pb='md' mb='md' borderBottom='4px solid' borderColor='gray.100' {...rest}>
      <Text fontSize='xl' fontWeight='bold' me='auto' textTransform='capitalize'>
        {sectionName}
      </Text>
      {children}
    </HStack>
  );
};

const MORE_ACTION_LABELS: Record<OrderMoreAction, string> = {
  resend_email: 'Resend email',
  resend_whatsapp: 'Resend WhatsApp',
  order_lifetime: 'Order lifetime',
  revert_order: 'Revert order',
  view_invoices: 'View attached invoices',
};

export const OrderDetailsScreen = () => {
  const { code } = useParams<{ code: string }>();
  const { isOpen: isOpenLifetimeModal, onClose: onCloseLifetimeModal, onOpen: onOpenLifetimeModal } = useDisclosure();

  const {
    isOpen: isOpenRevertOrderModal,
    onClose: onCloseRevertOrderModal,
    onOpen: onOpenRevertOrderModal,
  } = useDisclosure();

  const { isOpen: isOpenConfirmModal, onClose: onCloseConfirmModal, onOpen: onOpenConfirmModal } = useDisclosure();

  const { isOpen: isOpenCancelModal, onClose: onCloseCancelModal, onOpen: onOpenCancelModal } = useDisclosure();

  const { isOpen: isOpenEmailModal, onClose: onCloseEmailModal, onOpen: onOpenEmailModal } = useDisclosure();

  const { isOpen: isOpenWhatsappModal, onClose: onCloseWhatsappModal, onOpen: onOpenWhatsappModal } = useDisclosure();

  const { isOpen: isOpenInvoiceModal, onClose: onCloseInvoiceModal, onOpen: onOpenInvoiceModal } = useDisclosure();

  const { isLoading, isFetching, data: orderData } = useGetOrderDetailsApi(code);
  // Same query the comments card reads (shared by key), so this refetch only
  // reloads the comments, not the order.
  const { refetch: refetchComments, isFetching: isRefreshingComments } = useGetOrderCommentsApi(orderData?.id ?? '', {
    enabled: false,
  });

  /**
   * This function made for re-formatting the orderData response
   * because we have one missing status and we have to do come calculations logic to find it,
   * so instead of making some mess in the frontend code I will do the logic here and update the response
   * as I expected to get it from backend .. what to do :|
   */
  const _orderData = React.useMemo(() => {
    if (!orderData) return;

    // Clone order
    const _order = JSON.parse(JSON.stringify(orderData));

    // If order modified
    if (_order?.isModifiedBySupplier) {
      // Update order status value
      if (_order?.status === 'approved') {
        _order.status = 'modified';
      }
    }

    // Correct canceled status and history
    if (_order?.status === 'canceled' || _order?.status === 'rejected') {
      _order.status = 'canceled';

      const historyRejectedStatusIndex = _order?.history?.findIndex((item: any) => item.status === 'rejected');

      if (_order?.history?.[historyRejectedStatusIndex]) {
        _order.history[historyRejectedStatusIndex].status = 'canceled';
      }

      // Add rejected status
      if (_order?.lastUpdatedByUser?.id === _order?.supplier?.user?.id) {
        _order.status = 'rejected';

        const historyCanceledStatusIndex = _order?.history?.findIndex((item: any) => item.status === 'canceled');

        if (_order?.history?.[historyCanceledStatusIndex]) {
          _order.history[historyCanceledStatusIndex].status = 'rejected';
        }
      }
    }

    return _order;
  }, [orderData]);

  if (isLoading || isFetching) return <FullPageSpinner />;

  // Ops stand in for the supplier (backend spec §5.6). Raw status on purpose: the
  // display rewrite above turns `approved` into `modified`.
  const actions = orderDetailActions({
    rawStatus: orderData?.status,
    history: orderData?.history,
    canManage: ability.can('manage', 'Order'),
    invoiceCount: _orderData?.invoiceImages?.length ?? 0,
  });
  const previousStatus = previousOrderStatus(orderData?.status, orderData?.history);

  const openMoreAction: Record<OrderMoreAction, () => void> = {
    resend_email: onOpenEmailModal,
    resend_whatsapp: onOpenWhatsappModal,
    order_lifetime: onOpenLifetimeModal,
    revert_order: onOpenRevertOrderModal,
    view_invoices: onOpenInvoiceModal,
  };

  return (
    <>
      <VStack align='stretch' spacing='md' minW={0}>
        {/* Header: what the order is and what ops decide on; the rest sits behind More. */}
        <WhiteCard p='md'>
          <Flex wrap='wrap' align='center' gap='md'>
            <VStack align='start' spacing='2xs' me='auto' minW={0}>
              <HStack spacing='sm' flexWrap='wrap'>
                <Text fontSize='xl' fontWeight='bold'>
                  Order #{_orderData?.code}
                </Text>
                <StatusBadgeCell status={_orderData?.status} history={_orderData?.history} />
              </HStack>
              <Text fontSize='sm' color='gray.600'>
                Placed {dayjs(_orderData?.createdAt).format('DD-MM-YYYY')}
              </Text>
            </VStack>

            <HStack spacing='sm' flexWrap='wrap'>
              {actions.primary.confirm && (
                <Button colorScheme='green' onClick={onOpenConfirmModal}>
                  Confirm order
                </Button>
              )}
              {actions.primary.cancel && (
                <Button variant='outline' colorScheme='red' onClick={onOpenCancelModal}>
                  Cancel order
                </Button>
              )}
              {actions.more.length > 0 && (
                <Menu placement='bottom-end'>
                  <MenuButton
                    as={Button}
                    variant='outline'
                    rightIcon={<Icon path={mdiDotsHorizontal} size={0.75} />}
                    aria-label='more-actions'>
                    More
                  </MenuButton>
                  <MenuList>
                    {actions.more.map((action) => (
                      <MenuItem key={action} onClick={openMoreAction[action]}>
                        {MORE_ACTION_LABELS[action]}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </HStack>
          </Flex>
        </WhiteCard>

        {/* Details and comments side by side from the lg breakpoint (992 px viewport; stacked below), the
            items full width beneath; `minW=0` keeps a wide table from pushing the
            page into horizontal scroll (it scrolls inside its card). */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing='md' alignItems='stretch'>
          <WhiteCard p='md' minW={0} display='flex' flexDir='column'>
            <SectionHeader sectionName='Order Details' />
            <OrderDetailsSection order={_orderData} />
          </WhiteCard>

          <WhiteCard p='md' minW={0} display='flex' flexDir='column'>
            <SectionHeader sectionName='Order Comments'>
              <IconButtonWithTooltip
                label='Refresh comments'
                aria-label='refresh-comments'
                size='sm'
                variant='ghost'
                isLoading={isRefreshingComments}
                Icon={<Icon path={mdiRefresh} size={0.8} />}
                onClick={() => refetchComments()}
              />
            </SectionHeader>
            <OrderComments orderId={_orderData?.id} />
          </WhiteCard>
        </SimpleGrid>

        <WhiteCard p='md' minW={0} display='flex' flexDir='column'>
          <SectionHeader sectionName='Order Items' />
          <OrderItemsTable
            items={_orderData?.orderItems}
            totalItems={_orderData?.orderItems?.length}
            subtotal={_orderData?.subtotal}
            vat={_orderData?.vatValue}
            vatPercent={_orderData?.vatPercent}
            total={_orderData?.total}
            currencyCode={_orderData?.currencyCode}
            orderStatus={_orderData?.status}
          />
        </WhiteCard>
      </VStack>

      <RevertOrderModal
        isOpen={isOpenRevertOrderModal}
        onClose={onCloseRevertOrderModal}
        orderId={_orderData?.id}
        previousStatus={previousStatus}
      />

      <ConfirmOrderModal
        isOpen={isOpenConfirmModal}
        onClose={onCloseConfirmModal}
        orderId={_orderData?.id}
        code={_orderData?.code}
      />

      <CancelOrderModal
        isOpen={isOpenCancelModal}
        onClose={onCloseCancelModal}
        orderId={_orderData?.id}
        code={_orderData?.code}
      />

      <OrderLifeTimeModal isOpen={isOpenLifetimeModal} onClose={onCloseLifetimeModal} history={_orderData?.history} />

      <ResendEmailModal isOpen={isOpenEmailModal} onClose={onCloseEmailModal} orderId={_orderData?.id} />

      <ResendWhatsappModal isOpen={isOpenWhatsappModal} onClose={onCloseWhatsappModal} orderId={_orderData?.id} />

      <OrderInvoiceModal isOpen={isOpenInvoiceModal} onClose={onCloseInvoiceModal} images={_orderData?.invoiceImages} />
    </>
  );
};
